const CHAT_KEY = 'athlete_agent_chat';
const CONTENT_KEY = 'athlete_user_content';
/** Rail width in current grid column units (always 1 — thinner rail uses higher totalCols, e.g. 24). */
const RAIL_COLS = 1;
/** Fallback collapsed row height (12-col space); scaled when totalCols > 12. */
const BAR_H = 2;
const EPS = 1e-3;

/** Double x,y,w,h so a 12-col layout can be rendered on a 24-col grid (each column half as wide). */
export function doubleLayoutDimensions(layout) {
  if (!Array.isArray(layout)) return layout;
  return layout.map((item) => ({
    ...item,
    x: item.x * 2,
    y: item.y * 2,
    w: item.w * 2,
    h: item.h * 2,
  }));
}

/** Column intervals overlap or share an edge (required for adjacent chat|content). */
function colTouchOrOverlap(a, b) {
  return Math.min(a.x + a.w, b.x + b.w) > Math.max(a.x, b.x) - EPS;
}

/** Row intervals overlap or share an edge. */
function rowTouchOrOverlap(a, b) {
  return Math.min(a.y + a.h, b.y + b.h) > Math.max(a.y, b.y) - EPS;
}

/**
 * When My Content is collapsed on Zitius AI dashboard:
 * - Stacked full-width: chat + thin right rail (totalCols − RAIL_COLS wide chat).
 * - Side-by-side (content to the right): shrink content to RAIL_COLS, grow chat.
 * - Pass totalCols: 24 with {@link doubleLayoutDimensions} for a physically thinner rail vs cols=12.
 */
export function deriveZitiusAiCollapsedLayout(layout, collapsed, options = {}) {
  const totalCols = options.totalCols ?? 12;
  if (!collapsed || !Array.isArray(layout)) return layout;
  const chat = layout.find((i) => i.i === CHAT_KEY);
  const content = layout.find((i) => i.i === CONTENT_KEY);
  if (!chat || !content) return layout;

  const next = layout.map((item) => ({ ...item }));
  const chatItem = next.find((i) => i.i === CHAT_KEY);
  const contentItem = next.find((i) => i.i === CONTENT_KEY);
  if (!chatItem || !contentItem) return layout;

  const scale = totalCols / 12;
  const nearlyFullW = Math.ceil(11 * scale);
  const barHScaled = Math.max(1, Math.round(BAR_H * scale));

  const chatBottom = chatItem.y + chatItem.h;
  const stackedBelow =
    colTouchOrOverlap(chatItem, contentItem) && contentItem.y >= chatBottom - EPS;

  if (stackedBelow) {
    const sameFullWidth =
      chatItem.x === contentItem.x &&
      chatItem.w === contentItem.w &&
      chatItem.w >= nearlyFullW &&
      contentItem.w >= nearlyFullW;

    if (sameFullWidth) {
      const top = Math.min(chatItem.y, contentItem.y);
      const bottom = Math.max(chatItem.y + chatItem.h, contentItem.y + contentItem.h);
      const combinedH = bottom - top;
      chatItem.y = top;
      chatItem.h = combinedH;
      chatItem.w = totalCols - RAIL_COLS - chatItem.x;
      if (chatItem.w < 1) chatItem.w = 1;
      contentItem.y = top;
      contentItem.h = combinedH;
      contentItem.x = chatItem.x + chatItem.w;
      contentItem.w = Math.min(RAIL_COLS, Math.max(1, totalCols - contentItem.x));
      return next;
    }

    const newContentH = Math.min(barHScaled, Math.max(1, contentItem.h));
    const freed = Math.max(0, contentItem.h - newContentH);
    if (freed > 0) {
      chatItem.h += freed;
      contentItem.h = newContentH;
      contentItem.y = chatItem.y + chatItem.h;
    }
    return next;
  }

  const sideBySideRight =
    rowTouchOrOverlap(chatItem, contentItem) &&
    contentItem.x >= chatItem.x + chatItem.w - EPS &&
    colTouchOrOverlap(chatItem, contentItem);

  if (sideBySideRight) {
    const newContentW = RAIL_COLS;
    const freedW = Math.max(0, contentItem.w - newContentW);
    if (freedW > 0) {
      chatItem.w += freedW;
      contentItem.w = newContentW;
      contentItem.x = chatItem.x + chatItem.w;
    }
    return next;
  }

  return layout;
}
